/**
 * OrderSummary Component
 * Displays cart/order summary with pricing
 * Reusable across order flow and checkout pages
 */

import type { CartItem } from '../../types/widget/order';
import type { PricingConfig } from '../../services/provider.service';
import { PricingBreakdown } from '../widget/PricingBreakdown';

interface OrderSummaryProps {
  item: CartItem;
  pricing: PricingConfig;
  showEdit?: boolean;
  onEdit?: () => void;
}

/**
 * Format date for display
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get order type label
 */
function getOrderTypeLabel(orderType: string): string {
  const labels: Record<string, string> = {
    delivery: 'Delivery',
    swap: 'Swap',
    pickup: 'Pickup',
  };
  return labels[orderType] || orderType;
}

export function OrderSummary({ item, pricing, showEdit = false, onEdit }: OrderSummaryProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Order Summary</h2>
          {showEdit && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center min-h-[44px] px-3 text-sm font-medium text-primary hover:text-primary-700 focus:outline-none focus:underline"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Service type */}
        <div>
          <dt className="text-sm font-medium text-gray-500">Service</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {getOrderTypeLabel(item.orderType)} - {item.containerSize} Container
          </dd>
        </div>

        {/* Material type */}
        <div>
          <dt className="text-sm font-medium text-gray-500">Material Type</dt>
          <dd className="mt-1 text-sm text-gray-900">{item.materialType}</dd>
        </div>

        {/* Delivery date */}
        <div>
          <dt className="text-sm font-medium text-gray-500">Delivery Date</dt>
          <dd className="mt-1 text-sm text-gray-900">{formatDate(item.deliveryDate)}</dd>
        </div>

        {/* Service address */}
        <div>
          <dt className="text-sm font-medium text-gray-500">Service Address</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {item.serviceAddress.street}
            <br />
            {item.serviceAddress.city}, {item.serviceAddress.state}{' '}
            {item.serviceAddress.zipCode}
          </dd>
        </div>

        {/* Placement instructions (if provided) */}
        {item.placementInstructions && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Placement Instructions</dt>
            <dd className="mt-1 text-sm text-gray-900">{item.placementInstructions}</dd>
          </div>
        )}
      </div>

      {/* Pricing Breakdown */}
      <div className="bg-gray-50 px-4 py-4 border-t border-gray-200">
        <PricingBreakdown item={item} pricing={pricing} showDisclaimer={true} />
      </div>
    </div>
  );
}
